<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useTopicStore } from "@/stores/useTopicStore";
import { useUserStore } from "@/stores/useUserStore";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Componentes modais
import AddTopicForm from "./AddTopicForm.vue";
import EditTopicForm from "./EditTopicForm.vue";
import DeleteTopicForm from "./DeleteTopicForm.vue";

import TopicCardModal from "./TopicCardModal.vue";
import BatchDeleteTopicForm from "./BatchDeleteTopicForm.vue";
import SearchTopicInput from "./SearchTopicInput.vue";
import TopicHelpModal from "./TopicHelpModal.vue";

// Componentes de paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Store
const topicStore = useTopicStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCardModal = ref(false);
const selectedTopicId = ref(null);

const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA (renderiza somente a página após fetch paginado)
const topicsToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ REGRA SYS / VISUAL
// ====================================================================
function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}
function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}
function isCreatedBySys(form) {
  return normalizeUserName(form?.createdByUserName) === "sys";
}

function isRowSelectable(form) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = normalizeUserName(form?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
}

function sysProtectedTooltip(form) {
  if (!form) return "";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(form)) return "";
  return "Protegido (criado pelo Sys)";
}
// ====================================================================

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableOnPage = topicsToRender.value.filter((lf) =>
      isRowSelectable(lf)
    );
    if (selectableOnPage.length === 0) return false;

    return selectableOnPage.every((lf) =>
      topicStore.selectedTopicIds.includes(lf.topicId)
    );
  },
  set: (value) => {
    const selectableIdsOnPage = topicsToRender.value
      .filter((lf) => isRowSelectable(lf))
      .map((lf) => lf.topicId);

    const allIdsOnPage = topicsToRender.value.map((lf) => lf.topicId);

    if (value) {
      selectableIdsOnPage.forEach((id) => {
        if (!topicStore.selectedTopicIds.includes(id)) {
          topicStore.selectedTopicIds.push(id);
        }
      });
    } else {
      topicStore.selectedTopicIds = topicStore.selectedTopicIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

const anySelectableOnPage = computed(() =>
  topicsToRender.value.some((topic) => isRowSelectable(topic))
);

const canBatchActions = computed(() => {
  return (
    Array.isArray(topicStore.selectedTopicIds) &&
    topicStore.selectedTopicIds.length > 0
  );
});

function toggleRowDropdown(topicId) {
  openDropdownId.value = openDropdownId.value === topicId ? null : topicId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const topic = topicsToRender.value[index];
  if (!topic) return;

  selectedTopicId.value = topic.topicId;
  topicStore.setIdTopic(topic.topicId);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(form) {
  if (!isRowSelectable(form)) return;
  topicStore.toggleSelection(form.topicId);
}

// ====================================================================
// ✅ TOAST PADRÃO
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(3000);
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}
// ====================================================================

async function fetchTopicsPaged() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) topicsToRender.value = [];

    await topicStore.fetchTopicsPaged(
      currentPage.value,
      pageSize.value,
      topicStore.searchTerm
    );

    topicsToRender.value = Array.isArray(topicStore.topics)
      ? [...topicStore.topics]
      : [];

    isTableReady.value = true;

    if (!isLoggedSys() && topicsToRender.value.length) {
      const sysIdsOnPage = topicsToRender.value
        .filter((lf) => isCreatedBySys(lf))
        .map((lf) => lf.topicId);

      if (sysIdsOnPage.length) {
        topicStore.selectedTopicIds = topicStore.selectedTopicIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }

    if (topicStore.shouldForcePageOne) {
      topicStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await topicStore.fetchTopicsPaged(
          1,
          pageSize.value,
          topicStore.searchTerm
        );
        topicsToRender.value = Array.isArray(topicStore.topics)
          ? [...topicStore.topics]
          : [];

        if (!isLoggedSys() && topicsToRender.value.length) {
          const sysIdsOnPage = topicsToRender.value
            .filter((lf) => isCreatedBySys(lf))
            .map((lf) => lf.topicId);

          if (sysIdsOnPage.length) {
            topicStore.selectedTopicIds = topicStore.selectedTopicIds.filter(
              (id) => !sysIdsOnPage.includes(id)
            );
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar assuntos:", error?.message);
    showErrorToast("Erro ao carregar assuntos.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchTopicsPaged);

watch(
  () => topicStore.searchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchTopicsPaged();
    }
  }
);

watch(
  () => topicStore.topics,
  (val) => {
    if (!isTableReady.value) return;
    topicsToRender.value = Array.isArray(val) ? [...val] : [];

    if (!isLoggedSys() && topicsToRender.value.length) {
      const sysIdsOnPage = topicsToRender.value
        .filter((lf) => isCreatedBySys(lf))
        .map((lf) => lf.topicId);

      if (sysIdsOnPage.length) {
        topicStore.selectedTopicIds = topicStore.selectedTopicIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }
  }
);

function tryOpenBatchDeleteModal() {
  const selectedIds = topicStore.selectedTopicIds || [];

  if (selectedIds.length === 0) {
    showErrorToast("Nenhum assunto selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }

  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchTopicsPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  topicStore.searchTerm = "";
  topicStore.currentPage = 1;
  topicStore.shouldForcePageOne = true;

  topicStore.selectedTopicIds = [];
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedTopicId.value = null;

  topicsToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});

function indexSelector(index) {
  const topic = topicsToRender.value[index];
  if (!topic) return;
  topicStore.setIdTopic(topic.topicId);
}

async function handleTopicAdded() {
  if (currentPage.value !== 1) currentPage.value = 1;
  await fetchTopicsPaged();
}

async function handleTopicChanged() {
  await fetchTopicsPaged();
}

async function handleTopicDeleted() {
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  topicStore.selectedTopicIds = [];
  await fetchTopicsPaged();
}

function canManage(form) {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = (form?.createdByUserName ?? "").trim().toLowerCase();

  if (!creator) return true;

  return creator !== "sys";
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Assuntos</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie os assuntos utilizados para classificar os textos.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchTopicInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Assunto
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <ul class="p-0 m-0 list-none"></ul>
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anySelectableOnPage"
                  :class="{ 'disabled-item': !anySelectableOnPage }"
                />
              </th>
              <th style="width: 5%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 20%">Assunto</th>
              <th style="width: 30%">Descrição</th>
              <th style="width: 15%">Criador</th>
              <th style="width: 15%">Data Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(topic, index) in !isLoadingTable ? topicsToRender : []"
              :key="topic.topicId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{
 'checkbox-cell-disabled': !isRowSelectable(topic),
                }"
              >
                <input
                  type="checkbox"
                  :value="topic.topicId"
                  :checked="topicStore.selectedTopicIds.includes(topic.topicId)"
                  :disabled="!isRowSelectable(topic)"
                  :title="sysProtectedTooltip(topic)"
                  :class="[
 'checkbox-style',
                    { 'checkbox-disabled': !isRowSelectable(topic) },
                  ]"
                  @change="toggleSelection(topic)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(topic.topicId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === topic.topicId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(topic) }"
                      @click="canManage(topic) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(topic) }"
                      @click="canManage(topic) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="topic.topicId">
                  {{ topic.topicId }}
                </span>
              </td>

              <td class="cell-text-left">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="truncate" :title="toTitleCase(topic.topicName)">
                    {{ toTitleCase(topic.topicName) }}
                  </span>
                </div>
              </td>

              <td class="cell-text-left">
                <span :title="topic.description">
                  {{ topic.description }}
                </span>
              </td>

              <td class="cell-creator">
                <div
                  class="creator-cell"
                  :title="toTitleCase(topic.createdByUserName)"
                >
                  <template v-if="isCreatedBySys(topic)">
                    <span class="sys-badge" title="Criado pelo Sys">SYS</span>
                  </template>
                  <template v-else>
                    <span class="truncate">
                      {{ toTitleCase(topic.createdByUserName) }}
                    </span>
                  </template>
                </div>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(topic.updatedAt)">
                  {{ formatDateTimeForDisplay(topic.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable">
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando assuntos...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="topicsToRender.length === 0">
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum assunto encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (topicStore.totalCount ?? 0) > 0"
          class="topic-pagination-row"
        >
          <div class="topic-pagination-left">
            <span class="topic-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="topicStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="topicStore.totalCount ?? 0"
            :total-pages="topicStore.totalPages ?? 0"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddTopicForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @topic-added="handleTopicAdded"
    />
    <EditTopicForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="handleTopicChanged"
      @notify="handleChildNotify"
    />
    <TopicCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :topic-id="selectedTopicId"
      @close="showCardModal = false"
      @topic-updated="handleTopicChanged"
      @notify="handleChildNotify"
    />
    <DeleteTopicForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="handleTopicDeleted"
      @notify="handleChildNotify"
    />
    <BatchDeleteTopicForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleTopicDeleted"
      @notify="handleChildNotify"
    />
    <TopicHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

.cell-creator {
  text-align: center;
}
.cell-creator > .creator-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: none;
  margin: 0;
}
.cell-creator > .creator-cell .truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topic-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.topic-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.topic-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
}

</style>